import menuClasses from './_masthead_menu.module.scss';

/**
 * Custom menu component using Shoelace dropdown
 * 
 * @class
 * 
 * @property {Array} items - Array of menu items
 * @property {String} label - Label for the dropdown button
 * @property {String} theme - Theme of the dropdown (light, dark)
 */
export default class UaMastheadMenu extends HTMLElement {
  items = [];
  label = 'Menu';
  theme = 'light';
  iconPrefix;

  /** 
   * Attributes to watch for changes
   * @return {String[]}
   */
  static get observedAttributes() {
    return ['items', 'label', 'icon-prefix', 'theme'];
  }

  /** 
   * Object of UI classes
   * @return {Object}
   */
  get classes() {
    return {
      menuWrapper: menuClasses['c-masthead-menu__wrapper'],
      menuMastheadMenu: menuClasses['c-masthead-menu'],
      menuTriggerButton: menuClasses['c-masthead-menu__trigger-btn'],
      menuContainer: menuClasses['c-masthead-menu__menu-container'],
      menuContainerDivider: menuClasses['c-masthead-menu__menu-container-divider'],
      menuTriggerButtonIcon: menuClasses['c-masthead-menu__trigger-btn-icon'],
      menuItem: menuClasses['c-masthead-menu__menu-item'],
      menuLabel: menuClasses['c-masthead-menu__menu-label'],
    };
  }

  /**
   * Get the template for the menu
   * @return {String}
   */
  get template() {
    return `
      <div class="${this.classes.menuMastheadMenu}" theme="${this.theme}">
        <div class="${this.classes.menuWrapper}">
          <sl-button variant="default" size="medium" pill class="${this.classes.menuTriggerButton}">
            ${this.iconPrefix ? `<sl-icon slot="prefix" name="${this.iconPrefix}" library="untitled-ui" class="${this.classes.menuTriggerButtonIcon}"></sl-icon>` : ''}
            <sl-icon slot="suffix" name="chevron-down" library="untitled-ui" class="${this.classes.menuTriggerButtonIcon}"></sl-icon>  
            ${this.label}
          </sl-button>
          <nav class="${this.classes.menuContainer}">
            <ul class="c-plain-list-v3">
              ${this.getMenuItems()}
              ${this.getSubmenuItems()}
            </ul>
          </nav>
        </div>
      </div>
    `;
  }

  connectedCallback() {
    this.render();
  }

  render() {
    this.innerHTML = this.template;
  }

  /**
   * Returns menu items that don't have submenus
   * @return {String}
   */
  getMenuItems() {
    return this.items
      .filter(item => !item.submenu_items || item.submenu_items.length === 0)
      .map(item => `<li><a class="${this.classes.menuItem}" href="${item.link}">${item.label}</a></li>`)
      .join('');
  }

  /**
   * Returns menu items that have submenus
   * @return {String}
   */
  getSubmenuItems() {
    return this.items
      .filter(item => item.submenu_items && item.submenu_items.length > 0)
      .map(item => {
        const submenuItems = item.submenu_items
          .map(submenuItem => `<li><a class="${this.classes.menuItem}" href="${submenuItem.link}">${submenuItem.label}</a></li>`)
          .join('');

        return `<sl-divider class="${this.classes.menuContainerDivider}"></sl-divider>
                <h3 class="${this.classes.menuLabel}">${item.label}</h3>
                <ul class="">${submenuItems}</ul>`;
      })
      .join('');
  }

  attributeChangedCallback(attrName, oldValue, newValue) {
    if (oldValue === newValue) return;

    switch (attrName) {
      case 'items':
        try {
          this.items = JSON.parse(newValue);
        } catch (e) {
          this.items = [];
        }
        break;
      case 'label':
        this.label = newValue;
        break;
      case 'theme':
        this.theme = newValue;
        break;
      case 'icon-prefix':
        this.iconPrefix = newValue;
        break;
    }

    this.render();
  }
}
